"use client"

import type { DethatchingResult } from "@/lib/dethatching-calculator"

/**
 * The three glanceable result tiles (Method / Best window / Passes) with
 * hand-drawn line-art icon plates per the Claude Design. The dethatch rake is
 * the same artwork as the /tools grid icon in
 * components/tools/tool-illustration.tsx - keep them in sync if it changes
 * (same deliberate-duplication convention as the homepage chip strip).
 */

function MethodIcon({ verdict }: { verdict: DethatchingResult["verdict"] }) {
  if (verdict === "monitor") {
    // Aeration plugs + check: keep the layer, aerate instead.
    return (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <path d="M5 14c0-3 1-5 2-6M10 14c0-4 1-6 2-7M16 14c0-3 1-5 2-6M22 14c0-4 1-6 2-7M28 14c0-3 1-5 2-6" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M3 14h30" stroke="#0f1d14" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M3 14h30v14a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#0f1d14" strokeWidth="1.6" fill="#fef3c7" strokeLinejoin="round" opacity="0.5" />
        <circle cx="27" cy="27" r="6" fill="#fff" stroke="#15803d" strokeWidth="1.5" />
        <path d="M24.3 27l1.9 1.9 3.5-3.8" stroke="#15803d" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (verdict === "dethatch") {
    // Spring-tine rake pulling thatch (mirrors tool-illustration.tsx).
    return (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <path d="M31 4L21 15" stroke="#0f1d14" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M29 2.5l4 3.5" stroke="#0f1d14" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M15 15h8" stroke="#0f1d14" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M15.5 15c-.6 2-1.6 3.2-2.8 4M18 15c-.4 2-1.2 3.4-2.2 4.4M20.5 15c-.3 2-1 3.5-1.8 4.6M23 15c-.2 2-.7 3.6-1.4 4.8" stroke="#0f1d14" strokeWidth="1.3" strokeLinecap="round" />
        <path d="M4 24c1.2-1.4 2.8-1.8 4.2-1M7 25.5c1.4-1.2 3-1.4 4.4-.6M10.5 23.5c1.2-1 2.6-1.2 3.8-.5" stroke="#92400e" strokeWidth="1.4" strokeLinecap="round" opacity="0.85" />
        <path d="M3 20c1-2 2-3.5 2.5-5M6.5 20c.8-2 1.6-3.6 2-5.4M10 20c1-2 1.8-3.4 2.2-5" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M26 27c1-2 2-3.5 2.5-5M29.5 27c.8-2 1.6-3.4 2-5M23 27c.8-1.6 1.5-2.8 1.8-4" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M2 27.5h16M22 27.5h12" stroke="#15803d" strokeWidth="1.4" strokeLinecap="round" />
        <rect x="2" y="27.5" width="32" height="3.4" rx="1" fill="#92400e" opacity="0.28" />
        <path d="M2 33.5h32" stroke="#0f1d14" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    )
  }
  // Vertical mower blade wheel (urgent).
  return (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <circle cx="14" cy="18" r="9" fill="#fff" stroke="#0f1d14" strokeWidth="1.6" />
      <path d="M14 9v18M6.2 13.5l15.6 9M6.2 22.5l15.6-9" stroke="#0f1d14" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="14" cy="18" r="2" fill="#0f1d14" />
      <path d="M23 12l8-8M29 14l5-5" stroke="#0f1d14" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M25 28c1.5-1.8 3.5-2.2 5.2-1.2" stroke="#92400e" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <path d="M2 31c0-2 1-3 2-4M31 31c0-2 1-3 2-4" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 33.5h32" stroke="#0f1d14" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function CalendarIcon() {
  return (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <rect x="5" y="7" width="26" height="23" rx="2.6" stroke="#0f1d14" strokeWidth="1.6" fill="#fff" />
      <rect x="5" y="7" width="26" height="6" rx="2.6" fill="#16a34a" />
      <path d="M11 5v4M25 5v4" stroke="#0f1d14" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M9 18h3M15 18h3M27 18h1.5M9 23h3M27 23h1.5" stroke="#9ca3af" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="14" y="21" width="9" height="4.5" rx="2.2" fill="#bbf7d0" />
      <path d="M15.5 23.2h6" stroke="#15803d" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function PassesIcon() {
  return (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M5 10h22" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" />
      <path d="M27 10l-4-3M27 10l-4 3" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M31 18H9" stroke="#92400e" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
      <path d="M9 18l4-3M9 18l4 3" stroke="#92400e" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
      <path d="M5 26h22" stroke="#0f1d14" strokeWidth="2" strokeLinecap="round" opacity="0.35" />
      <path d="M27 26l-4-3M27 26l-4 3" stroke="#0f1d14" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.35" />
    </svg>
  )
}

function Tile({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-3.5 py-3">
      <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</span>
        <span className="mt-0.5 block text-sm font-bold leading-snug text-gray-900">{value}</span>
      </span>
    </div>
  )
}

export function MethodTiles({ result, windowLabel }: { result: DethatchingResult; windowLabel: string }) {
  return (
    <div className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
      <Tile icon={<MethodIcon verdict={result.verdict} />} label="Method" value={result.methodLabel} />
      <Tile icon={<CalendarIcon />} label="Best window" value={windowLabel} />
      <Tile icon={<PassesIcon />} label="Passes" value={result.passes} />
    </div>
  )
}
